import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, MessageSquare, Palette, Calculator, ClipboardCheck, Factory, Truck, Wrench, CheckSquare, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

const processSteps = [
  {
    number: "01",
    icon: MessageSquare,
    title: "Requirement Understanding",
    description: "We begin with a detailed discussion to understand your workspace needs, team size, workflow patterns, and budget parameters.",
    deliverables: ["Site survey report", "Requirement document", "Initial consultation"],
  },
  {
    number: "02",
    icon: Palette,
    title: "Design Consultation",
    description: "Our design team creates customized layouts optimized for your space, incorporating your brand aesthetics and functional requirements.",
    deliverables: ["2D floor plans", "3D visualizations", "Material samples"],
  },
  {
    number: "03",
    icon: Calculator,
    title: "Budgeting",
    description: "We provide transparent, detailed cost estimates covering materials, manufacturing, logistics, and installation with no hidden charges.",
    deliverables: ["Detailed quotation", "Cost breakdown", "Payment schedule"],
  },
  {
    number: "04",
    icon: ClipboardCheck,
    title: "Shop Drawing Approvals",
    description: "Precise shop drawings are prepared for your review and approval, ensuring every dimension and specification is confirmed before production.",
    deliverables: ["Technical shop drawings", "Specification sheets", "Approval sign-off"],
  },
  {
    number: "05",
    icon: Factory,
    title: "Manufacturing",
    description: "Production begins in our state-of-the-art facility with strict quality control at every stage of the manufacturing process.",
    deliverables: ["Progress updates", "Quality inspection reports", "Dispatch schedule"],
  },
  {
    number: "06",
    icon: Truck,
    title: "Dispatch",
    description: "Finished products are carefully packed and dispatched with full tracking, ensuring safe and timely delivery to your project site.",
    deliverables: ["Packing list", "Dispatch tracking", "Delivery schedule"],
  },
  {
    number: "07",
    icon: Wrench,
    title: "Installation",
    description: "Our experienced installation team ensures seamless setup with minimal disruption to your business operations.",
    deliverables: ["Installation plan", "Site coordination", "Daily progress reports"],
  },
  {
    number: "08",
    icon: CheckSquare,
    title: "Final Handover",
    description: "Complete quality inspection, snag rectification, and formal handover with all documentation and warranty certificates.",
    deliverables: ["Completion certificate", "Warranty cards", "User manual"],
  },
  {
    number: "09",
    icon: Headphones,
    title: "After Sales Support",
    description: "Dedicated support team for maintenance, repairs, and any future expansion or modification requirements.",
    deliverables: ["24/7 support line", "Annual maintenance plans", "Spare parts availability"],
  },
];

export default function ProcessPage() {
  useCanonical("https://ezeefitofficesystems.in/process");
  useMetaDescription("Ezeefit's 9-step office furniture execution — consultation, design, manufacturing, delivery, installation & handover. Mumbai, Thane, pan-India corporates.");
  useEffect(() => {
    document.title = "Office Furniture Execution Process Mumbai | End to End Office Setup Solutions | Ezeefit";
  }, []);

  return (
    <div className="pt-20" data-testid="process-page">
      <Helmet>
        <meta name="description" content="Ezeefit's 9-step office furniture execution — consultation, design, manufacturing, delivery, installation & handover. Mumbai, Thane, pan-India corporates." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/process" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "HowTo",
        "name": "How Ezeefit Executes Corporate Office Furniture Projects",
        "description": "Ezeefit's systematic 9-step process for furnishing corporate offices — from initial consultation to post-installation support.",
        "totalTime": "P6W",
        "step": [
          {"@type": "HowToStep", "position": 1, "name": "Requirement Understanding", "text": "Detailed discussion of workspace needs — space, headcount, workflow, brand guidelines, and budget parameters."},
          {"@type": "HowToStep", "position": 2, "name": "Design & Space Planning", "text": "3D layouts and floor plans optimized for productivity and aesthetics using professional design software."},
          {"@type": "HowToStep", "position": 3, "name": "Budgeting & Quotation", "text": "Transparent, detailed pricing with no hidden costs. Itemized quotation covering all furniture and services."},
          {"@type": "HowToStep", "position": 4, "name": "Shop Drawing Approvals", "text": "Precise manufacturing drawings approved before production begins to ensure exact specifications."},
          {"@type": "HowToStep", "position": 5, "name": "Manufacturing", "text": "CNC-precision manufacturing in our Bhiwandi factory with automated edge banding and panel saws."},
          {"@type": "HowToStep", "position": 6, "name": "Quality Inspection", "text": "Every piece inspected against ISO 9001:2015 quality standards before dispatch to ensure consistent quality."},
          {"@type": "HowToStep", "position": 7, "name": "Dispatch & Delivery", "text": "Protected packaging and professional logistics across India."},
          {"@type": "HowToStep", "position": 8, "name": "Installation & Handover", "text": "Expert installation team ensures seamless, disruption-free setup at your office."},
          {"@type": "HowToStep", "position": 9, "name": "After-Sales Support", "text": "Dedicated support team for warranty, maintenance, and any post-installation requirements."}
        ]
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://ezeefitofficesystems.in"},
          {"@type": "ListItem", "position": 2, "name": "Process", "item": "https://ezeefitofficesystems.in/process"}
        ]
      }) }} />
      {/* Hero */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
            
            <div className="text-center">
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                How We Work
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Our Execution Process
              </h1>
              <p className="text-xl text-[#94A3B8] max-w-2xl mx-auto">
                A systematic 9-step approach that ensures on-time delivery, 
                quality excellence, and complete transparency — from our{" "}
                <Link to="/manufacturing" className="text-[#F97316] underline hover:text-orange-400">Bhiwandi manufacturing facility</Link>{" "}
                to your office.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#E2E8F0] hidden md:block" />

            <div className="space-y-12">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="md:pl-24">
                    {/* Number indicator */}
                    <div className="absolute left-0 top-0 hidden md:flex items-center justify-center w-16 h-16 bg-[#0F172A] text-white font-mono font-bold text-lg z-10">
                      {step.number}
                    </div>

                    <div className="p-8 bg-[#F8FAFC] border border-[#E2E8F0] card-hover">
                      <div className="flex items-start gap-6">
                        <div className="w-14 h-14 bg-[#F97316] flex items-center justify-center shrink-0">
                          <step.icon className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex-1">
                          <span className="font-mono text-sm text-[#F97316] md:hidden mb-3 block">
                            Step {step.number}
                          </span>
                          <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                            {step.title}
                          </h3>
                          <p className="text-[#64748B] mb-6">{step.description}</p>
                          <div>
                            <span className="text-sm font-semibold text-[#0F172A] mb-3 block">
                              Deliverables:
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {step.deliverables.map((item) => (
                                <span
                                  key={item}
                                  className="text-xs font-mono text-[#64748B] bg-white px-3 py-1 border border-[#E2E8F0]"
                                >
                                  {item}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]">
              Our Commitment to You
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: "On-Time Delivery", description: "95% of our projects are delivered on or before the committed timeline." },
              { title: "Transparent Pricing", description: "No hidden costs. The price we quote is the price you pay." },
              { title: "Quality Assurance", description: "Every piece passes through 12-point quality inspection before dispatch." },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 bg-white border border-[#E2E8F0] text-center"
              >
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{item.title}</h3>
                <p className="text-[#64748B]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-[#94A3B8] mb-8">
            Experience our streamlined execution process firsthand. See our{" "}
            <Link to="/case-studies" className="text-[#F97316] underline hover:text-orange-400">completed projects</Link>{" "}
            or hear from our{" "}
            <Link to="/testimonials" className="text-[#F97316] underline hover:text-orange-400">satisfied clients</Link>.
          </p>
          <Button
            asChild
            className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-10 py-6 font-semibold uppercase tracking-wider"
          >
            <Link to="/contact">
              Begin Step 1
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      <ExploreMore currentPage="process" />
    </div>
  );
}
